import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { 
  Search, 
  BookOpen, 
  HelpCircle, 
  TrendingUp, 
  Clock,
  ArrowRight,
  Zap,
  Settings,
  AlertTriangle,
  Download
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Wiki - Stellar Blade Mods Knowledge Base" },
    { 
      name: "description", 
      content: "Comprehensive wiki and knowledge base for Stellar Blade mods. Find installation guides, troubleshooting tips, FAQ, and advanced modding tutorials." 
    },
    { name: "keywords", content: "stellar blade,wiki,knowledge base,faq,mod guide,installation,troubleshooting" },
    { name: "robots", content: "index,follow" },
    { name: "language", content: "en" },
    { rel: "canonical", href: "https://stellarblademod.com/wiki" },
    
    // Language alternates
    { tagName: "link", rel: "alternate", hrefLang: "en", href: "https://stellarblademod.com/wiki" },
    { tagName: "link", rel: "alternate", hrefLang: "ja", href: "https://stellarblademod.com/ja/wiki" },
    { tagName: "link", rel: "alternate", hrefLang: "ko", href: "https://stellarblademod.com/ko/wiki" },
    { tagName: "link", rel: "alternate", hrefLang: "x-default", href: "https://stellarblademod.com/wiki" },
    
    // Open Graph
    { property: "og:title", content: "Wiki - Stellar Blade Mods Knowledge Base" },
    { property: "og:description", content: "Comprehensive wiki and knowledge base for Stellar Blade mods. Find installation guides, troubleshooting tips, FAQ, and advanced modding tutorials." },
    { property: "og:url", content: "https://stellarblademod.com/wiki" },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: "en_US" },
    { property: "og:site_name", content: "Stellar Blade Mods" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Wiki - Stellar Blade Mods Knowledge Base" },
    { name: "twitter:description", content: "Comprehensive wiki and knowledge base for Stellar Blade mods. Find installation guides, troubleshooting tips, FAQ, and advanced modding tutorials." },
    
    { httpEquiv: "content-language", content: "en" },
  ];
};

export default function Wiki() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Hero Section */}
      <div className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Wiki
              </span>
              <br />
              <span className="text-2xl md:text-3xl text-gray-300">
                Knowledge Base <span className="text-amber-400 text-lg md:text-xl">(under construction)</span>
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Your comprehensive guide to Stellar Blade modding. Find installation guides, 
              troubleshooting solutions, and expert tips all in one place.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search wiki articles..."
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Main Categories */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Knowledge Base */}
          <Link to="/wiki/knowledge-base" className="group">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-all duration-300 hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-400/25">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg mr-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Knowledge Base <span className="text-amber-400 text-lg">(under construction)</span></h2>
                  <p className="text-gray-400">In-depth guides and tutorials</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                Comprehensive guides covering game mechanics, mod installation, 
                advanced techniques, and development resources.
              </p>
              <div className="flex items-center text-cyan-400 group-hover:text-cyan-300">
                <span className="mr-2">Explore Knowledge Base</span>
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          {/* FAQ */}
          <Link to="/wiki/faq" className="group">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-all duration-300 hover:border-purple-400/50 hover:shadow-lg hover:shadow-purple-400/25">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-purple-400 to-pink-500 rounded-lg mr-4">
                  <HelpCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">FAQ <span className="text-amber-400 text-lg">(under construction)</span></h2>
                  <p className="text-gray-400">Quick answers to common questions</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                Find instant solutions to the most frequently asked questions 
                about modding, installation issues, and technical support.
              </p>
              <div className="flex items-center text-purple-400 group-hover:text-purple-300">
                <span className="mr-2">Browse FAQ</span>
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>

        {/* Quick Access Categories */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Quick Access</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Installation Guide */}
            <Link to="/wiki/knowledge-base/installation" className="group">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-green-400/50">
                <div className="p-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-lg mb-4 w-fit">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Installation</h3>
                <p className="text-gray-400 text-sm">Step-by-step mod installation guides</p>
              </div>
            </Link>

            {/* Troubleshooting */}
            <Link to="/wiki/knowledge-base/troubleshooting" className="group">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-yellow-400/50">
                <div className="p-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg mb-4 w-fit">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Troubleshooting</h3>
                <p className="text-gray-400 text-sm">Fix common issues and errors</p>
              </div>
            </Link>

            {/* File Security */}
            <Link to="/wiki/knowledge-base/file-security" className="group">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-red-400/50">
                <div className="p-3 bg-gradient-to-r from-red-400 to-pink-500 rounded-lg mb-4 w-fit">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">File Security</h3>
                <p className="text-gray-400 text-sm">Scan and verify mod files safely</p>
              </div>
            </Link>

            {/* Tools */}
            <Link to="/mods/mod-hub" className="group">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-indigo-400/50">
                <div className="p-3 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-lg mb-4 w-fit">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Tools</h3>
                <p className="text-gray-400 text-sm">Essential modding tools and resources</p>
              </div>
            </Link>
          </div>
        </div>

{/* Popular Articles - Commented out */}
        {/*
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">Popular Articles</h2>
            <div className="flex items-center text-cyan-400">
              <TrendingUp className="w-5 h-5 mr-2" />
              <span>Most viewed this week</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Complete Mod Installation Guide",
                category: "Installation",
                views: "15.2k",
                readTime: "8 min"
              },
              {
                title: "Fixing Mod Conflicts and Load Order",
                category: "Troubleshooting", 
                views: "12.8k",
                readTime: "12 min"
              },
              {
                title: "Essential Modding Tools Setup",
                category: "Tools",
                views: "9.4k",
                readTime: "6 min"
              }
            ].map((article, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-sm">
                    {article.category}
                  </span>
                  <div className="flex items-center text-gray-400 text-sm">
                    <Clock className="w-4 h-4 mr-1" />
                    {article.readTime}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {article.title}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">{article.views} views</span>
                  <Link to="#" className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                    Read more →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
        */}

        {/* Recent Updates - Commented out */}
        {/* 
        <div>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">Recent Updates</h2>
            <div className="flex items-center text-purple-400">
              <Clock className="w-5 h-5 mr-2" />
              <span>Latest additions</span>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <div className="space-y-6">
              {[
                {
                  title: "New: Advanced Texture Replacement Guide",
                  date: "2 days ago",
                  type: "New Article"
                },
                {
                  title: "Updated: System Requirements and Compatibility",
                  date: "5 days ago", 
                  type: "Update"
                },
                {
                  title: "Added: Community Mod Development Guidelines",
                  date: "1 week ago",
                  type: "New Article"
                }
              ].map((update, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-white/10 last:border-b-0">
                  <div>
                    <h4 className="text-white font-medium mb-1">{update.title}</h4>
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-1 bg-purple-400/20 text-purple-400 rounded text-xs">
                        {update.type}
                      </span>
                      <span className="text-gray-400 text-sm">{update.date}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        </div>
        */}
      </div>
    </div>
  );
} 